import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { EASE, goToTesterSignup } from "@/lib/site";
import { Smartphone, ArrowDown } from "lucide-react";
import { VersionBadge } from "./VersionBadge";
import { OpenAppButton, WaitlistButton } from "./Buttons";
import { Phone } from "./Phone";
import { BuddyToast } from "./BuddyToast";

const LINES = [["Show up.", ""], ["Put in", ""], ["the work.", "text-grad"]];

const MaskedLine = ({ text, cls, i }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className={`block ${cls}`}
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

const useTilt = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 110, damping: 16 });
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 110, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };
  return { rotateX, rotateY, onMove, onLeave };
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const tilt = useTilt();

  return (
    <section id="top" ref={ref} className="grain relative overflow-hidden pt-28 pb-20 lg:pt-36 lg:pb-28" onMouseMove={tilt.onMove} onMouseLeave={tilt.onLeave}>
      <div className="pointer-events-none absolute -left-40 top-10 h-[520px] w-[520px] rounded-full bg-[#ff6a3d]/20 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <VersionBadge />
          <h1 data-testid="hero-title" className="font-display text-[4.2rem] font-black uppercase leading-[0.86] tracking-tight sm:text-8xl lg:text-[8.5rem]">
            {LINES.map(([t, c], i) => <MaskedLine key={t} text={t} cls={c} i={i} />)}
          </h1>
          <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, delay: 0.6, ease: EASE }} className="mt-8 max-w-xl text-base leading-relaxed text-[#b3b8c1] md:text-lg">
            Spotter is your free gym buddy. It plans your week, shows you how to do all 225 exercises, and tracks your food and progress. Bring a mate: one shared plan, your own weights.
          </motion.p>
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, delay: 0.75, ease: EASE }} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <OpenAppButton testId="hero-cta-open-app" />
            <WaitlistButton testId="hero-cta-waitlist" label="Get notified at launch" />
          </motion.div>
          <motion.button
            type="button"
            onClick={goToTesterSignup}
            data-testid="hero-tester-link"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="group mt-5 inline-flex items-center gap-2 rounded-full border border-[#4ade80]/30 bg-[#4ade80]/[0.07] py-1.5 pl-2 pr-3.5 text-sm text-[#d1fae5] transition-[border-color,background-color] duration-300 hover:border-[#4ade80]/60 hover:bg-[#4ade80]/[0.12]"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#4ade80]/15"><Smartphone className="h-3.5 w-3.5 text-[#4ade80]" /></span>
            Android testers wanted
            <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
          </motion.button>
          <p className="mt-4 text-xs text-[#9ca3af]">No sign-up, no password, no ads. Just a code.</p>
        </div>

        <div className="relative flex min-h-[540px] items-center justify-center lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, delay: 0.3, ease: EASE }} className="absolute inset-y-0 right-0 w-[82%] overflow-hidden rounded-[2rem]">
            <motion.img style={{ y: imgY }} src="/images/hero.jpg" alt="Athlete lifting a barbell in a dark gym" className="photo-treat h-[125%] w-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0e0f12] via-[#0e0f12]/20 to-[#ff6a3d]/25 mix-blend-normal" />
          </motion.div>
          <motion.div style={{ y: phoneY }} className="relative z-10 -ml-[22%] sm:-ml-[30%]">
            <motion.div style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: "preserve-3d" }} initial={{ y: 80, opacity: 0, rotate: -6 }} animate={{ y: 0, opacity: 1, rotate: -4 }} transition={{ duration: 1.2, delay: 0.7, ease: EASE }}>
              <Phone testId="hero-phone" src="/images/app-today-alex.webp" alt="Spotter home screen: Alex and Jess training together, today's Legs workout" />
            </motion.div>
            <BuddyToast />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
